import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import EsitoBadge from "../components/EsitoBadge";
import api, { API } from "../lib/api";
import { Toaster, toast } from "sonner";
import { formatTitle } from "../lib/usePageTitle";

function ShareRow({ titolo }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const waText = encodeURIComponent(`${titolo} — Pronto alla Vendita\n${url}`);
  const waHref = `https://wa.me/?text=${waText}`;
  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Impossibile copiare il link");
    }
  };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="share-row">
      <a
        href={waHref}
        target="_blank"
        rel="noreferrer"
        data-testid="share-whatsapp"
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white text-sm px-4 py-2.5 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="18" height="18" aria-hidden="true" fill="currentColor">
          <path d="M19.11 17.2c-.28-.14-1.66-.82-1.91-.91-.26-.09-.44-.14-.63.14-.19.28-.72.91-.88 1.1-.16.19-.32.21-.6.07-.28-.14-1.18-.44-2.24-1.39-.83-.74-1.39-1.65-1.55-1.93-.16-.28-.02-.43.12-.57.12-.12.28-.32.42-.48.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.51-.86-2.07-.23-.55-.46-.47-.63-.48h-.54c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34s1.01 2.72 1.15 2.9c.14.19 1.98 3.02 4.79 4.24.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.66-.68 1.89-1.34.23-.66.23-1.22.16-1.34-.07-.12-.26-.19-.54-.33zM16.02 3C9.4 3 4 8.4 4 15.02c0 2.12.55 4.19 1.6 6.01L4 29l8.14-1.55A11.98 11.98 0 0 0 16.02 27C22.6 27 28 21.6 28 15.02 28 8.4 22.6 3 16.02 3zm0 21.94c-1.79 0-3.53-.48-5.05-1.39l-.36-.21-4.83.92.92-4.7-.24-.38a10.02 10.02 0 0 1-1.53-5.34c0-5.52 4.49-10.01 10.01-10.01 5.52 0 10.01 4.49 10.01 10.01 0 5.52-4.49 10.01-10.01 10.01z"/>
        </svg>
        Condividi su WhatsApp
      </a>
      <button
        type="button"
        onClick={copyLink}
        data-testid="share-copy"
        className={`inline-flex items-center gap-2 text-sm px-4 py-2.5 border transition-colors ${copied ? "bg-[#7D2E3F] text-white border-[#7D2E3F]" : "bg-white text-[#3A3D42] border-[#D8D5D0] hover:border-[#7D2E3F] hover:text-[#7D2E3F]"}`}
      >
        {copied ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Link copiato
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1 1"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1-1"/></svg>
            Copia link
          </>
        )}
      </button>
    </div>
  );
}

const ESITO_TEXT = {
  pronto: "L'immobile è trasferibile e non risultano criticità rilevanti da dichiarare, secondo le informazioni raccolte nel Protocollo.",
  criticita: "L'immobile è trasferibile. Le criticità Rilevanti sono dichiarate qui sotto e verranno sottoscritte dall'acquirente prima della proposta d'acquisto.",
};

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [showDocReq, setShowDocReq] = useState(false);
  const [showContact, setShowContact] = useState(false);
  useEffect(() => {
    api.get(`/public/properties/${id}`).then((r) => setP(r.data)).catch(() => setErr("Immobile non trovato o non pubblicato"));
  }, [id]);

  useEffect(() => {
    if (p?.titolo) document.title = formatTitle(p.titolo);
  }, [p?.titolo]);

  // Dynamic Open Graph / Twitter Card meta tags for social sharing
  useEffect(() => {
    if (!p) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const ogImage = `${backend}/api/public/properties/${p.id}/og-image.png`;
    const shareTitle = `${p.titolo} | Pronto alla Vendita`;
    const zone = [p.comune, p.provincia].filter(Boolean);
    const zoneStr = zone[0] && zone[1] && zone[0].toLowerCase() === zone[1].toLowerCase()
      ? zone[0]
      : zone.join(", ");
    const descBits = [];
    if (zoneStr) descBits.push(zoneStr);
    if (p.tipologia) descBits.push(p.tipologia);
    if (p.superficie) descBits.push(`${p.superficie} m²`);
    const shareDesc = descBits.length
      ? `${descBits.join(" · ")} — Immobile Pronto alla Vendita, verificato secondo il Protocollo.`
      : "Immobile Pronto alla Vendita, verificato secondo il Protocollo.";
    const pageUrl = window.location.href;

    const upsertMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };

    const created = [];
    created.push(upsertMeta("property", "og:title", shareTitle));
    created.push(upsertMeta("property", "og:description", shareDesc));
    created.push(upsertMeta("property", "og:image", ogImage));
    created.push(upsertMeta("property", "og:image:width", "1200"));
    created.push(upsertMeta("property", "og:image:height", "630"));
    created.push(upsertMeta("property", "og:url", pageUrl));
    created.push(upsertMeta("property", "og:type", "website"));
    created.push(upsertMeta("name", "twitter:card", "summary_large_image"));
    created.push(upsertMeta("name", "twitter:title", shareTitle));
    created.push(upsertMeta("name", "twitter:description", shareDesc));
    created.push(upsertMeta("name", "twitter:image", ogImage));

    return () => {
      // Reset OG image on navigation back to defaults defined in index.html
      const desc = "Il Protocollo Pronto alla Vendita verifica ogni immobile in 10 aree tecnico-legali e ne valorizza il prezzo prima della vendita. RX CASA garante.";
      upsertMeta("property", "og:title", "Pronto alla Vendita | Il Protocollo per la vendita immobiliare");
      upsertMeta("property", "og:description", desc);
      upsertMeta("property", "og:image", `${backend.replace(/\/$/, "")}/brand/favicon_512.png`);
    };
  }, [p]);

  if (err) return (
    <div className="paper-bg min-h-screen">
      <PublicNav />
      <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">{err}</h1>
        <Link to="/galleria" className="btn-secondary mt-8">Torna alla Galleria</Link>
      </div>
    </div>
  );
  if (!p) return <div className="paper-bg min-h-screen"><PublicNav /><div className="py-32 text-center">Caricamento…</div></div>;

  const photoUrl = (ph) => ph?.external_url || (ph?.path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${ph.path}` : null);
  const criticities = (p.verifiche || []).filter((v) => v.rilevante && v.criticita && v.criticita.trim());

  return (
    <div className="paper-bg" data-testid="property-detail">
      <Toaster position="top-right" richColors />
      <PublicNav />
      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-10">
        <Link to="/galleria" className="text-sm text-[#5C5657] hover:text-[#7D2E3F]">← Galleria</Link>
      </section>

      <section className="max-w-[1240px] mx-auto px-6 md:px-10 pt-8 pb-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {p.photos?.[0] && (
            <div className="relative aspect-[4/3] bg-[#F4F3F1] border border-[#E6E4E0] overflow-hidden">
              <img src={photoUrl(p.photos[0])} alt={p.titolo} className="w-full h-full object-cover" />
              {(p.esito === "pronto" || p.esito === "criticita") && (
                <img
                  src="/brand/bollino-512.png"
                  alt="Pronto alla Vendita"
                  title={p.esito === "criticita" ? "Pronto alla Vendita con Criticità Dichiarate" : "Pronto alla Vendita"}
                  className="absolute top-4 right-4 w-16 h-16 md:w-20 md:h-20 [filter:drop-shadow(0_3px_6px_rgba(0,0,0,0.25))_drop-shadow(0_10px_22px_rgba(0,0,0,0.30))]"
                  data-testid="detail-photo-bollino"
                />
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="label-uppercase">{p.tipologia} · {p.comune} ({p.provincia})</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">{p.titolo}</h1>
          <div className="mt-3">
            {p.stato_pubblicazione === "venduto" ? (
              <span className="badge-stamp badge-non_conforme" data-testid="public-status-badge" data-stato="venduto">Venduto{p.data_vendita ? ` · ${String(p.data_vendita).slice(0,10).split("-").reverse().join("/")}` : ""}</span>
            ) : (
              <span className="badge-stamp badge-pronto" data-testid="public-status-badge" data-stato="in_vendita">Disponibile</span>
            )}
          </div>

          {(p.esito === "pronto" || p.esito === "criticita") && (
            <div className="mt-6 border border-[#E6E4E0] p-5 bg-[#FDFDFC]" data-testid="esito-block">
              <div className="flex items-center gap-5">
                <img
                  src="/brand/bollino-512.png"
                  alt="Bollino Pronto alla Vendita by RX CASA"
                  className="w-20 h-20 shrink-0"
                  data-testid="detail-bollino"
                />
                <div className="flex-1">
                  <div className="label-uppercase mb-2">Esito del Protocollo</div>
                  <EsitoBadge esito={p.esito} testId="detail-badge" />
                </div>
              </div>
              {p.fascicolo_code && (
                <div className="mt-4 pt-4 border-t border-[#E6E4E0]" data-testid="fascicolo-code-block">
                  <div className="label-uppercase mb-1">Fascicolo n°</div>
                  <div className="font-mono text-sm tracking-wider text-[#3A3D42]" data-testid="fascicolo-code">{p.fascicolo_code}</div>
                </div>
              )}
            </div>
          )}

          {/* Share buttons */}
          <ShareRow titolo={p.titolo} data-testid="share-row" />

          <p className="text-[#5C5657] mt-6 leading-relaxed text-sm">{ESITO_TEXT[p.esito] || ""}</p>

          <div className="mt-8 border border-[#E6E4E0] p-6">
            <div className="label-uppercase mb-3">Contatta l'agenzia</div>
            <div className="flex items-start gap-4">
              {(p.agency?.logo_url || p.agency?.logo) && (
                <div className="w-16 h-16 bg-white border border-[#E6E4E0] flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    src={((p.agency.logo_url || p.agency.logo).startsWith("http") ? "" : process.env.REACT_APP_BACKEND_URL) + (p.agency.logo_url || p.agency.logo)}
                    alt={`Logo ${p.agency.name}`}
                    className="w-full h-full object-contain p-1"
                    data-testid="agency-logo"
                  />
                </div>
              )}
              <div className="flex-1">
                <div className="font-serif text-xl">{p.agency?.name}</div>
                {p.agente_nome && <div className="text-sm text-[#5C5657] mt-2">Agente di riferimento: <span className="text-[#3A3D42]">{p.agente_nome}</span></div>}
              </div>
            </div>
            <p
              data-testid="detail-commercial-note"
              className="mt-5 pt-5 border-t border-[#E6E4E0] text-sm text-[#5C5657] leading-relaxed"
            >
              Per informazioni commerciali contatta l'agenzia tramite il modulo qui sotto: la richiesta viene tracciata e inoltrata direttamente al referente.
            </p>
            <button
              type="button"
              onClick={() => setShowContact(true)}
              data-testid="contact-cta-agency"
              className="btn-primary mt-5 w-full text-center"
            >
              Contatta l'agenzia
            </button>
          </div>
          {showDocReq && <DocRequestModal propertyId={p.id} propertyTitolo={p.titolo} onClose={() => setShowDocReq(false)} variant="docs" />}
          {showContact && <DocRequestModal propertyId={p.id} propertyTitolo={p.titolo} onClose={() => setShowContact(false)} variant="contact" />}
        </div>
      </section>

      {/* ANAGRAFICA — fascia dati orizzontale */}
      <section className="border-t border-[#E6E4E0] paper-alt-bg">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-12">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="font-serif text-3xl">Anagrafica dell'immobile</h2>
          </div>

          <dl className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-y-6 gap-x-8 text-sm">
            <div><dt className="label-uppercase">Superficie</dt><dd className="mt-1 font-serif text-lg">{p.superficie} m²</dd></div>
            {p.locali ? <div><dt className="label-uppercase">Locali</dt><dd className="mt-1 font-serif text-lg">{p.locali}</dd></div> : null}
            {p.camere ? <div><dt className="label-uppercase">Camere</dt><dd className="mt-1 font-serif text-lg">{p.camere}</dd></div> : null}
            {p.bagni ? <div><dt className="label-uppercase">Bagni</dt><dd className="mt-1 font-serif text-lg">{p.bagni}</dd></div> : null}
            {p.piano ? <div><dt className="label-uppercase">Piano</dt><dd className="mt-1 font-serif text-lg">{p.piano}</dd></div> : null}
            <div><dt className="label-uppercase">Classe energ.</dt><dd className="mt-1 font-serif text-lg">{p.classe_energetica || "—"}</dd></div>
            {p.anno ? <div><dt className="label-uppercase">Anno</dt><dd className="mt-1 font-serif text-lg">{p.anno}</dd></div> : null}
            {p.riscaldamento ? <div><dt className="label-uppercase">Riscaldamento</dt><dd className="mt-1 text-sm">{p.riscaldamento}</dd></div> : null}
            {p.stato_manutentivo ? <div><dt className="label-uppercase">Stato manut.</dt><dd className="mt-1 text-sm">{ {nuovo:"Nuovo",buono:"Buono",da_ristrutturare:"Da ristrutturare"}[p.stato_manutentivo] }</dd></div> : null}
            {p.arredato ? <div><dt className="label-uppercase">Arredato</dt><dd className="mt-1 text-sm">{ {si:"Sì",no:"No",parziale:"Parziale"}[p.arredato] }</dd></div> : null}
          </dl>

          {(p.cantina || p.box_auto || p.terrazzo || p.balcone || p.ascensore) && (
            <>
              <div className="label-uppercase mt-10 mb-4">Pertinenze e caratteristiche</div>
              <div className="flex flex-wrap gap-2">
                {p.cantina && <span className="badge-stamp border-[#5C5657] text-[#5C5657]">Cantina</span>}
                {p.box_auto && <span className="badge-stamp border-[#5C5657] text-[#5C5657]">Box auto</span>}
                {p.terrazzo && <span className="badge-stamp border-[#5C5657] text-[#5C5657]">Terrazzo</span>}
                {p.balcone && <span className="badge-stamp border-[#5C5657] text-[#5C5657]">Balcone</span>}
                {p.ascensore && <span className="badge-stamp border-[#5C5657] text-[#5C5657]">Ascensore</span>}
              </div>
            </>
          )}

          {p.highlights?.length > 0 && (
            <>
              <div className="label-uppercase mt-10 mb-4">Punti forti</div>
              <ul className="grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm">
                {p.highlights.map((h, i) => (
                  <li key={i} className="flex items-baseline gap-3"><span className="text-[#7D2E3F] font-serif text-lg">·</span><span className="text-[#3A3D42]">{h}</span></li>
                ))}
              </ul>
            </>
          )}

          {p.photos?.length > 1 && (
            <>
              <div className="label-uppercase mt-10 mb-4">Altre foto</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {p.photos.slice(1, 5).map((ph) => (
                  <div key={ph.id} className="relative aspect-[4/3] bg-white border border-[#E6E4E0] overflow-hidden">
                    <img src={photoUrl(ph)} alt="" className="w-full h-full object-cover" />
                    {(p.esito === "pronto" || p.esito === "criticita") && (
                      <img
                        src="/brand/bollino-512.png"
                        alt="Pronto alla Vendita"
                        className="absolute top-2 right-2 w-8 h-8 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.22))_drop-shadow(0_4px_10px_rgba(0,0,0,0.25))]"
                        data-testid={`detail-thumb-bollino-${ph.id}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="border-t border-[#E6E4E0] paper-alt-bg">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-16">
          <div className="label-uppercase mb-2">Le dieci aree di verifica</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-10">Cosa abbiamo controllato</h2>
          <div className="grid md:grid-cols-2 gap-x-16">
            {(p.verifiche || []).map((v, i) => (
              <div key={v.label} className="py-4 border-b border-[#E6E4E0] flex items-baseline gap-6">
                <span className="font-serif text-[#7D2E3F] w-6">{String(i+1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[#3A3D42]">{v.label}</span>
                    <span className="label-uppercase">{v.status === "completata" ? "Verificata" : v.status === "in_corso" ? "In corso" : "Da fare"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-[#E6E4E0] bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-xl">
              <div className="label-uppercase text-[#7D2E3F]">Documentazione completa</div>
              <p className="mt-2 text-[#3A3D42] leading-relaxed text-sm">
                Vuoi ricevere il dossier con gli esiti delle dieci aree di verifica? Ti mettiamo in contatto con l'agenzia partner della zona.
              </p>
            </div>
            <button
              data-testid="request-docs-btn"
              onClick={() => setShowDocReq(true)}
              className="btn-secondary md:w-auto w-full text-center whitespace-nowrap"
            >
              Richiedi la documentazione
            </button>
          </div>

          {criticities.length > 0 && (
            <div className="mt-14">
              <h3 className="font-serif text-2xl mb-6 text-[#91590F]">Criticità Rilevanti dichiarate</h3>
              <div className="space-y-4">
                {criticities.map((v) => (
                  <div key={v.label} className="border border-[#E6E4E0] p-6 bg-white">
                    <div className="label-uppercase text-[#91590F]">{v.label}</div>
                    <p className="text-sm text-[#3A3D42] mt-2">{v.criticita}</p>
                    <div className="mt-4 text-sm">
                      <span className="label-uppercase">Tempi</span>
                      <div className="mt-1">{v.tempi || "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function DocRequestModal({ propertyId, propertyTitolo, onClose, variant = "docs" }) {
  const isContact = variant === "contact";
  const labelUpper = isContact ? "Contatta l'agenzia" : "Richiesta documentazione";
  const successTitle = isContact ? "Richiesta inviata." : "Richiesta ricevuta.";
  const successBody = isContact
    ? "La tua richiesta è stata registrata e inoltrata all'agenzia partner che segue questo immobile. Sarai ricontattato direttamente dall'agenzia nel più breve tempo possibile."
    : "Un referente Pronto alla Vendita ti contatterà a breve per condividere la documentazione tramite l'agenzia partner della zona.";
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", messaggio: isContact ? "" : "", consenso_privacy: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso_privacy) { toast.error("Accetta l'informativa privacy"); return; }
    setLoading(true);
    try {
      const payload = { ...form, tipo: isContact ? "contatto_agenzia" : "richiesta_documentazione" };
      if (isContact && !payload.messaggio) payload.messaggio = "[Contatto agenzia dalla scheda immobile]";
      await api.post(`/public/properties/${propertyId}/document-request`, payload);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#3A3D42]/60 flex items-start justify-center overflow-auto p-4" data-testid={isContact ? "contact-modal" : "doc-request-modal"}>
      <div className="bg-white border border-[#E6E4E0] w-full max-w-lg mt-16 mb-16" onClick={(e) => e.stopPropagation()}>
        <div className="p-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="label-uppercase text-[#7D2E3F]">{labelUpper}</div>
              <h2 className="font-serif text-3xl mt-2 leading-tight">{propertyTitolo}</h2>
            </div>
            <button onClick={onClose} className="text-[#5C5657] text-xl">×</button>
          </div>
          {sent ? (
            <div className="py-8 text-center">
              <div className="font-serif text-2xl text-[#7D2E3F]">{successTitle}</div>
              <p className="text-[#5C5657] mt-3">{successBody}</p>
              <button onClick={onClose} className="btn-secondary mt-6">Chiudi</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div><label className="label-uppercase">Nome e cognome *</label><input required data-testid="dr-nome" value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} className="editorial-input" /></div>
              <div><label className="label-uppercase">Email *</label><input required type="email" data-testid="dr-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="editorial-input" /></div>
              <div><label className="label-uppercase">Telefono *</label><input required data-testid="dr-tel" value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} className="editorial-input" /></div>
              <div><label className="label-uppercase">Messaggio (opzionale)</label><textarea rows="3" data-testid="dr-msg" value={form.messaggio} onChange={(e) => setForm({...form, messaggio: e.target.value})} className="editorial-input" /></div>
              <label className="flex items-start gap-3 text-xs text-[#5C5657] mt-2">
                <input required type="checkbox" data-testid="dr-consenso" checked={form.consenso_privacy} onChange={(e) => setForm({...form, consenso_privacy: e.target.checked})} className="mt-1" />
                <span>Accetto l'informativa privacy e il trattamento dei dati per essere contattato in merito a questo immobile.</span>
              </label>
              <button type="submit" disabled={loading} data-testid="dr-submit" className="btn-primary w-full mt-4">{loading ? "Invio…" : "Invia richiesta"}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
